import { isSourceStale, type SourceDto } from '@vision360/contracts';
import { AlertTriangle, WifiOff } from 'lucide-react';
import { Component, type ErrorInfo, type ReactNode, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { useLiveState } from '@/features/live/live-stream';
import { cn } from '@/lib/utils';
import { widgetName, widgetRegistry } from '../_registry';
import { absoluteTime, ago, widgetText } from './format';
import { useSources } from './hooks';
import type { AnyWidget, Size, WidgetDataState } from './types';

/**
 * Every widget instance renders inside this frame (TechSpec §9.1.1): error
 * boundary, props validation, the loading / empty / error / stale states and
 * the feeding sources. `Render` only ever sees data — a widget cannot forget a
 * state, and one failing widget never blanks the dashboard (rule 7).
 */

export interface WidgetFrameProps {
  type: string;
  props: Record<string, unknown>;
  size: Size;
  /** Header controls (edit mode). */
  actions?: ReactNode;
  /** Shown on invalid configuration, when the dashboard can open the editor. */
  onConfigure?: () => void;
  now?: () => number;
  registry?: ReadonlyMap<string, AnyWidget>;
}

function Shell({
  title,
  badge,
  actions,
  footer,
  children,
  busy,
}: {
  title: string;
  badge?: ReactNode;
  actions?: ReactNode;
  footer?: ReactNode;
  children: ReactNode;
  busy?: boolean;
}) {
  return (
    <section
      aria-label={title}
      aria-busy={busy}
      className="flex h-full flex-col overflow-hidden rounded-lg border bg-card text-card-foreground shadow-xs"
    >
      <header className="flex min-h-9 items-center gap-2 border-b px-3 py-1.5">
        <h2 className="min-w-0 flex-1 truncate text-sm font-semibold">{title}</h2>
        {badge}
        {actions}
      </header>
      <div className="relative min-h-0 flex-1 overflow-auto p-3">{children}</div>
      {footer}
    </section>
  );
}

function Problem({
  message,
  onRetry,
  onConfigure,
}: {
  message: string;
  onRetry?: (() => void) | undefined;
  onConfigure?: (() => void) | undefined;
}) {
  const { t } = useTranslation('widgets');
  return (
    <div role="alert" className="flex h-full flex-col items-start justify-center gap-2 text-sm">
      <p className="flex items-center gap-2 text-destructive">
        <AlertTriangle aria-hidden="true" className="h-4 w-4 shrink-0" />
        {message}
      </p>
      <div className="flex gap-2">
        {onRetry && (
          <Button size="sm" variant="outline" onClick={onRetry}>
            {t('retry')}
          </Button>
        )}
        {onConfigure && (
          <Button size="sm" variant="outline" onClick={onConfigure}>
            {t('configure')}
          </Button>
        )}
      </div>
    </div>
  );
}

class Boundary extends Component<
  { fallback: (error: Error) => ReactNode; children: ReactNode },
  { error: Error | null }
> {
  override state = { error: null as Error | null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    // biome-ignore lint/suspicious/noConsole: the stack of a crashed widget goes where a developer will look
    console.error('widget crashed', error, info.componentStack);
  }

  override render() {
    return this.state.error ? this.props.fallback(this.state.error) : this.props.children;
  }
}

function Loading() {
  const { t } = useTranslation('widgets');
  return (
    <output aria-live="polite" className="flex items-center gap-2 text-sm text-muted-foreground">
      <Spinner />
      {t('loading')}
    </output>
  );
}

/** Stale when a feeding source is unhealthy or late, or the stream is down (VS-015, VS-043). */
function useHealth(sourceIds: string[], now: () => number) {
  const live = useLiveState();
  const sources = useSources();
  const feeding = (sources.data?.sources ?? []).filter((s) => sourceIds.includes(s.id));
  const unhealthy = feeding.find(
    (s: SourceDto) => s.status !== 'active' || isSourceStale(s, now()),
  );
  return { offline: live === 'reconnecting', unhealthy, feeding };
}

function Instance({
  widget,
  props,
  size,
  title,
  actions,
  now,
}: {
  widget: AnyWidget;
  props: unknown;
  size: Size;
  title: string;
  actions?: ReactNode;
  now: () => number;
}) {
  const { t } = useTranslation('widgets');
  const state: WidgetDataState<unknown> = widget.useData(props);
  const { offline, unhealthy, feeding } = useHealth(widget.sourceIds(props), now);
  const own = widgetText(widget.type);

  let badge: ReactNode = null;
  if (offline) {
    badge = (
      <span className="inline-flex items-center gap-1 rounded-sm bg-muted px-1.5 py-0.5 text-xs text-destructive">
        <WifiOff aria-hidden="true" className="h-3 w-3" />
        {t('offline')}
      </span>
    );
  } else if (unhealthy && state.status !== 'loading') {
    const label = state.lastUpdated
      ? t('stale', { when: ago(state.lastUpdated, now()) })
      : unhealthy.status !== 'active'
        ? t('sourceDown', { status: t(`status.${unhealthy.status}`) })
        : t('staleNoData');
    badge = (
      <span
        className="rounded-sm bg-muted px-1.5 py-0.5 text-xs text-destructive"
        title={state.lastUpdated ? absoluteTime.format(state.lastUpdated) : undefined}
      >
        {label}
      </span>
    );
  }

  let body: ReactNode;
  if (state.status === 'loading') body = <Loading />;
  else if (state.status === 'error') body = <Problem message={t('error')} onRetry={state.retry} />;
  else if (state.status === 'empty' || state.data === null) {
    body = (
      <p className="text-sm text-muted-foreground">{own('empty', { defaultValue: t('empty') })}</p>
    );
  } else {
    const Render = widget.Render;
    body = (
      <Suspense fallback={<Loading />}>
        <Render props={props} data={state.data} size={size} />
      </Suspense>
    );
  }

  const footer =
    feeding.length > 0 ? (
      <p className="truncate border-t px-3 py-1 text-xs text-muted-foreground">
        {t('fedBy', { names: feeding.map((s) => s.name).join(', ') })}
      </p>
    ) : null;

  return (
    <Shell
      title={title}
      badge={badge}
      actions={actions}
      footer={footer}
      busy={state.status === 'loading'}
    >
      <div className={cn('h-full', badge && offline && 'opacity-80')}>{body}</div>
    </Shell>
  );
}

export function WidgetFrame({
  type,
  props,
  size,
  actions,
  onConfigure,
  now = Date.now,
  registry = widgetRegistry,
}: WidgetFrameProps) {
  const { t } = useTranslation('widgets');
  const widget = registry.get(type);
  const label = typeof props['label'] === 'string' && props['label'] ? props['label'] : null;

  if (!widget) {
    return (
      <Shell title={label ?? type} actions={actions}>
        <Problem message={t('unknown', { type })} />
      </Shell>
    );
  }
  const title = label ?? widgetName(type);
  const parsed = widget.propsSchema.safeParse(props);
  if (!parsed.success) {
    return (
      <Shell title={title} actions={actions}>
        <Problem message={t('invalid')} onConfigure={onConfigure} />
      </Shell>
    );
  }
  return (
    <Boundary
      // A new configuration gets a fresh chance.
      key={JSON.stringify(props)}
      fallback={() => (
        <Shell title={title} actions={actions}>
          <Problem message={t('crashed')} onConfigure={onConfigure} />
        </Shell>
      )}
    >
      <Instance
        widget={widget}
        props={parsed.data}
        size={size}
        title={title}
        actions={actions}
        now={now}
      />
    </Boundary>
  );
}
